import Button from "@components/Button"
import InfoBubble from "@components/InfoBubble"
import Layout from "@components/Layout"
import PageHeading from "@components/PageHeading"
import PasswordInput from "@components/PasswordInput"
import { setPasswordInSessionStorage } from "@helpers/sessionStorage"
import { validatePassword } from "@helpers/validations"
import { IsUserSignedInLoaderData } from "@loaders/isUserSignedInLoader"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Form,
  Navigate,
  useLoaderData,
  useLocation,
  useNavigate,
} from "react-router-dom"

export default function PasswordPage() {
  const [password, setPassword] = useState("")
  const [passwordValid, setPasswordValid] = useState(true)

  const loaderData = useLoaderData() as IsUserSignedInLoaderData | null
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  // Determine the base path from current location (handles both /onboarding and /onboarding/v1)
  const basePath = location.pathname.replace(/\/password$/, "")

  // If user is already signed in, skip to preparing
  if (loaderData?.isUserSignedIn) {
    return <Navigate to="/sign-up/preparing" />
  }

  // Validate the password on each render
  const isPasswordValid = validatePassword(password)

  // Handle form submission
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (isPasswordValid) {
      setPasswordInSessionStorage(password)
      navigate(`${basePath}/email`)
    } else {
      setPasswordValid(false)
    }
  }

  return (
    <Layout>
      <PageHeading>{t("pages.signUp.password.heading")}</PageHeading>

      {/* Info bubble */}
      <InfoBubble variant="yellow">
        {t(
          "pages.signUp.password.subheading",
          "The password must be at least 6 characters long",
        )}
      </InfoBubble>

      {/* Form */}
      <Form className="custom-form px-4" onSubmit={handleSubmit}>
        <PasswordInput
          autoFocus
          invalid={!passwordValid}
          onChange={(event) => setPassword(event.target.value)}
          value={password}
        />

        <div className="mt-auto pt-6">
          <Button
            disabled={password.length < 1}
            type="submit"
            title={t("shared.continue")}
          />
        </div>
      </Form>
    </Layout>
  )
}
